/**
 * Pointer-reactive polish for service pages: magnetic cards, spotlight,
 * floating stage parallax, and approach timeline progress.
 */
export function bindServicesMotion(root) {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  if (reduced) return () => {};

  const cleanups = [];

  if (fine) {
    cleanups.push(bindMagneticCards(root));
    cleanups.push(bindStageParallax(root));
  }

  cleanups.push(bindApproachTimeline(root));
  cleanups.push(bindScrollFloats(root));

  return () => cleanups.forEach((fn) => fn?.());
}

function bindMagneticCards(root) {
  const cards = [
    ...root.querySelectorAll(
      ".services-approach__card, .services-insight, .services-faq, .services-stage__window, .services-stage__panel"
    ),
  ];

  const onMove = (event) => {
    const card = event.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    const rx = (0.5 - y) * 10;
    const ry = (x - 0.5) * 12;

    card.style.setProperty("--spot-x", `${x * 100}%`);
    card.style.setProperty("--spot-y", `${y * 100}%`);
    card.style.setProperty("--tilt-x", `${rx}deg`);
    card.style.setProperty("--tilt-y", `${ry}deg`);
    card.classList.add("is-hot");
  };

  const onLeave = (event) => {
    const card = event.currentTarget;
    card.style.setProperty("--tilt-x", "0deg");
    card.style.setProperty("--tilt-y", "0deg");
    card.classList.remove("is-hot");
  };

  cards.forEach((card) => {
    card.addEventListener("pointermove", onMove);
    card.addEventListener("pointerleave", onLeave);
  });

  return () => {
    cards.forEach((card) => {
      card.removeEventListener("pointermove", onMove);
      card.removeEventListener("pointerleave", onLeave);
    });
  };
}

function bindStageParallax(root) {
  const stage = root.querySelector(".services-stage");
  if (!stage) return () => {};

  const floats = [...stage.querySelectorAll(".services-stage__orb")];
  let raf = 0;
  let tx = 0;
  let ty = 0;
  let cx = 0;
  let cy = 0;

  const onMove = (event) => {
    const rect = stage.getBoundingClientRect();
    tx = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
    ty = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
    if (!raf) raf = requestAnimationFrame(tick);
  };

  const tick = () => {
    cx += (tx - cx) * 0.08;
    cy += (ty - cy) * 0.08;

    stage.style.setProperty("--par-x", `${cx * 12}px`);
    stage.style.setProperty("--par-y", `${cy * 10}px`);

    floats.forEach((el, i) => {
      const depth = 8 + i * 6;
      el.style.setProperty("--mx", `${cx * depth}px`);
      el.style.setProperty("--my", `${cy * depth}px`);
    });

    if (Math.abs(tx - cx) > 0.001 || Math.abs(ty - cy) > 0.001) {
      raf = requestAnimationFrame(tick);
    } else {
      raf = 0;
    }
  };

  stage.addEventListener("pointermove", onMove);
  return () => {
    stage.removeEventListener("pointermove", onMove);
    if (raf) cancelAnimationFrame(raf);
  };
}

function bindApproachTimeline(root) {
  const section = root.querySelector(".services-approach");
  const line = root.querySelector(".services-approach__progress");
  if (!section || !line) return () => {};

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting) return;
      section.classList.add("is-playing");
      observer.disconnect();
    },
    { threshold: 0.35 }
  );

  observer.observe(section);
  return () => observer.disconnect();
}

function bindScrollFloats(root) {
  const banner = root.querySelector(".services-banner");
  const watermark = root.querySelector(".services-banner__watermark");
  if (!banner || !watermark) return () => {};

  const onScroll = () => {
    const rect = banner.getBoundingClientRect();
    const progress = Math.min(Math.max((-rect.top) / Math.max(rect.height, 1), 0), 1);
    watermark.style.transform = `translate3d(${progress * 40}px, ${progress * -30}px, 0)`;
    watermark.style.opacity = String(0.12 - progress * 0.08);
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();
  return () => window.removeEventListener("scroll", onScroll);
}
